/** @format */

import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { Eye, EyeOff } from "lucide-react";
import { useSession } from "../context/SessionContext";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

const Login = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { session } = useSession();

  useEffect(() => {
    if (session) {
      navigate("/");
    }
  }, [session, navigate]);

  useEffect(() => {
    const savedLang = localStorage.getItem("preferredLanguage") || "es";
    i18n.changeLanguage(savedLang).catch((err) =>
      console.error("Error applying language from localStorage:", err)
    );
  }, [i18n]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast.error(error.message || t("error_login"));
      return;
    }

    const { data: userData, error: userError } = await supabase
      .from("assistant")
      .select("must_change_password")
      .eq("email", email)
      .single();

    if (userError) {
      console.error("Error obteniendo datos del usuario:", userError);
      toast.error(t("error_loading_user_data"));
    } else if (userData?.must_change_password) {
      console.log("Reset the password");
      navigate("/reset-password");
      return;
    }

    toast.success(t("login_success"));
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-14">
          <img
            src="/logo.svg"
            alt="Omniloy Logo"
            className="w-12 h-12 mx-auto mb-4"
          />
        </div>

        <div>
          <div className="text-left mb-6">
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              {t("titleLogin")}
            </h1>
            <p className="text-sm text-gray-700">{t("subtitle")}</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block text-lg font-bold text-black mb-2"
              >
                {t("emailLabel")}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 bg-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder={t("emailPlaceholder")}
                required
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-lg font-bold text-black mb-2"
              >
                {t("passwordLabel")}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border bg-white border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder={t("passwordPlaceholder")}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-[#105BDB] hover:underline"
              >
                {t("forgot_password")}
              </Link>
            </div>

            <button
              type="submit"
              disabled={!email || !password}
              className="w-full bg-[#105BDB] hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-md transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
            >
              {t("loginButton")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
